import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, FileText, X, Trash2, ChevronDown, Send, CheckCircle2, XCircle, Clock, ArrowRightLeft, Download, Grid3x3 } from "lucide-react";

const STATUS_STYLES = {
  draft: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
  sent: "text-[#60A5FA] bg-[#60A5FA]/10 border-[#60A5FA]/20",
  accepted: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  rejected: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  expired: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

function fmt(n, ccy = "GBP") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(Number(n) || 0).toFixed(2)}`;
  }
}

const EMPTY_ITEM = { description: "", quantity: 1, unit_price: 0 };
const EMPTY_FORM = {
  title: "", client_name: "", deal_id: "", project_id: "",
  currency: "GBP", tax_rate: "0", valid_until: "", notes: "",
  items: [{ ...EMPTY_ITEM }],
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [deals, setDeals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [matrixRows, setMatrixRows] = useState([]);
  const [matrixSaving, setMatrixSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [qRes, dRes, pRes, aRes] = await Promise.all([
        api.get("/quotes"),
        api.get("/deals").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/activity-types").catch(() => ({ data: [] })),
      ]);
      setQuotes(qRes.data || []);
      setDeals(dRes.data || []);
      setProjects(pRes.data || []);
      setActivityTypes(aRes.data || []);
    } catch {
      toast.error("Failed to load quotes");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (q) => {
    try {
      const { data } = await api.get(`/quotes/${q.quote_id}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load quote");
    }
  };

  const dealName = (id) => deals.find(d => d.deal_id === id)?.name || "—";
  const projName = (id) => projects.find(p => p.project_id === id)?.name || "—";

  const openMatrix = () => {
    setMatrixRows(
      detail.estimation_matrix && detail.estimation_matrix.length > 0
        ? detail.estimation_matrix.map(r => ({ ...r }))
        : [{ activity_type_id: "", label: "", hours: 0, rate: 0 }]
    );
    setMatrixOpen(true);
  };
  const addMatrixRow = () => setMatrixRows(rows => [...rows, { activity_type_id: "", label: "", hours: 0, rate: 0 }]);
  const removeMatrixRow = (idx) => setMatrixRows(rows => rows.filter((_, i) => i !== idx));
  const updateMatrixRow = (idx, patch) => setMatrixRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const pickActivityType = (idx, activityTypeId) => {
    const at = activityTypes.find(a => a.activity_type_id === activityTypeId);
    updateMatrixRow(idx, {
      activity_type_id: activityTypeId || "",
      label: at ? at.name : matrixRows[idx].label,
      rate: at ? at.default_rate : matrixRows[idx].rate,
    });
  };
  const matrixTotal = matrixRows.reduce((s, r) => s + (parseFloat(r.hours) || 0) * (parseFloat(r.rate) || 0), 0);

  const applyMatrix = async () => {
    const rows = matrixRows.filter(r => r.label.trim());
    if (rows.length === 0) return toast.error("Add at least one row");
    setMatrixSaving(true);
    try {
      const { data } = await api.post(`/quotes/${detail.quote_id}/estimation-matrix`, {
        rows: rows.map(r => ({
          activity_type_id: r.activity_type_id || undefined, label: r.label,
          hours: parseFloat(r.hours) || 0, rate: parseFloat(r.rate) || 0,
        })),
      });
      setDetail(data);
      toast.success("Estimation matrix applied to line items");
      setMatrixOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply matrix");
    } finally {
      setMatrixSaving(false);
    }
  };

  const updateItem = (idx, patch) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const items = form.items.filter(i => i.description.trim());
    if (items.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      await api.post("/quotes", {
        title: form.title, client_name: form.client_name,
        deal_id: form.deal_id || undefined, project_id: form.project_id || undefined,
        currency: form.currency, tax_rate: parseFloat(form.tax_rate) || 0,
        valid_until: form.valid_until || undefined, notes: form.notes,
        line_items: items.map(i => ({ description: i.description, quantity: parseFloat(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
      });
      toast.success("Quote created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create quote");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${detail.quote_id}/status`, { status });
      setDetail(data);
      toast.success(`Quote marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const convertToInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${detail.quote_id}/convert-to-invoice`);
      toast.success(`Invoice ${data.invoice_number} created`);
      const { data: refreshed } = await api.get(`/quotes/${detail.quote_id}`);
      setDetail(refreshed);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to convert quote");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const BASE = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE}/api/quotes/${detail.quote_id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quote-${detail.quote_number || detail.quote_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete quote "${detail.title}"?`)) return;
    try {
      await api.delete(`/quotes/${detail.quote_id}`);
      toast.success("Quote deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete quote");
    }
  };

  if (detail) {
    const editable = detail.status === "draft" || detail.status === "sent";
    return (
      <DarkPage><div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => setDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs text-[#7d8590] font-mono mb-1">{detail.quote_number}</div>
            <h1 className="text-2xl font-bold text-white">{detail.title}</h1>
            <p className="text-sm text-[#7d8590] mt-1">
              {detail.client_name || "No client"} · {dealName(detail.deal_id)} · {projName(detail.project_id)}
            </p>
          </div>
          <span className={`text-xs px-3 py-1.5 rounded-full border capitalize ${STATUS_STYLES[detail.status]}`}>{detail.status}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {detail.status === "draft" && (
            <button disabled={busy} onClick={() => changeStatus("sent")} className="text-xs px-3 py-1.5 rounded-full border border-[#60A5FA]/30 text-[#60A5FA] bg-[#60A5FA]/10 flex items-center gap-1">
              <Send className="w-3 h-3" /> Mark sent
            </button>
          )}
          {detail.status === "sent" && (
            <>
              <button disabled={busy} onClick={() => changeStatus("accepted")} className="text-xs px-3 py-1.5 rounded-full border border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Accepted
              </button>
              <button disabled={busy} onClick={() => changeStatus("rejected")} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Rejected
              </button>
              <button disabled={busy} onClick={() => changeStatus("expired")} className="text-xs px-3 py-1.5 rounded-full border border-amber-500/20 text-amber-400 bg-amber-500/10 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Expired
              </button>
            </>
          )}
          {detail.status === "accepted" && !detail.converted_to_invoice_id && (
            <button disabled={busy} onClick={convertToInvoice} className="text-xs px-3 py-1.5 rounded-full border border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10 flex items-center gap-1">
              <ArrowRightLeft className="w-3 h-3" /> Convert to invoice
            </button>
          )}
          {detail.converted_to_invoice_id && (
            <span className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590]">Converted to invoice</span>
          )}
          <button onClick={downloadPdf} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white flex items-center gap-1">
            <Download className="w-3 h-3" /> Download PDF
          </button>
          {editable && (
            <button onClick={openMatrix} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white flex items-center gap-1">
              <Grid3x3 className="w-3 h-3" /> Estimation matrix
            </button>
          )}
          {editable && (
            <button onClick={remove} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 flex items-center gap-1 ml-auto">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
        </div>

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">Line items</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Description", "Qty", "Unit price", "Total"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(detail.line_items || []).map(li => (
                <tr key={li.line_item_id} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-2.5 text-white">{li.description}</td>
                  <td className="px-4 py-2.5 text-[#7d8590]">{li.quantity}</td>
                  <td className="px-4 py-2.5 text-[#7d8590]">{fmt(li.unit_price, detail.currency)}</td>
                  <td className="px-4 py-2.5 text-white">{fmt(li.line_total, detail.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-[#21262d] flex flex-col items-end gap-1 text-sm">
            <div className="text-[#7d8590]">Subtotal: <span className="text-white">{fmt(detail.subtotal, detail.currency)}</span></div>
            <div className="text-[#7d8590]">Tax ({((detail.tax_rate || 0) * 100).toFixed(0)}%): <span className="text-white">{fmt(detail.tax_amount, detail.currency)}</span></div>
            <div className="text-base font-bold text-white">Total: {fmt(detail.total_amount, detail.currency)}</div>
          </div>
        </div>

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">Status history</div>
          <div className="divide-y divide-[#21262d]">
            {[...(detail.status_history || [])].reverse().map((h, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[h.status]}`}>{h.status}</span>
                <span className="text-xs text-[#7d8590]">{h.changed_by_name} · {new Date(h.changed_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Quotes</h1>
          <p className="text-sm text-[#7d8590] mt-1">Priced proposals a client accepts before there's a contract or an invoice.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Quote
        </Button>
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">No quotes yet. Create one to send a priced proposal to a client.</div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Number", "Title", "Client", "Status", "Total"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.quote_id} onClick={() => openDetail(q)} className="border-b border-[#21262d] last:border-0 cursor-pointer hover:bg-[#111827]">
                  <td className="px-4 py-3 text-[#7d8590] font-mono text-xs">{q.quote_number}</td>
                  <td className="px-4 py-3 text-white flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#7d8590]" /> {q.title}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{q.client_name || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[q.status]}`}>{q.status}</span></td>
                  <td className="px-4 py-3 text-white">{fmt(q.total_amount, q.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Quote</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Website redesign"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Client name</label>
                  <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Valid until</label>
                  <input type="date" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Deal (optional)</label>
                  <select value={form.deal_id} onChange={e => setForm(f => ({ ...f, deal_id: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option value="">None</option>
                    {deals.map(d => <option key={d.deal_id} value={d.deal_id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Project (optional)</label>
                  <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option value="">None</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["USD", "GBP", "EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Tax rate (e.g. 0.1 = 10%)</label>
                  <input type="number" step="0.01" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-[#7d8590]">Line items</label>
                  <button onClick={addItem} className="text-xs text-[#00F5D4] hover:underline">+ Add line</button>
                </div>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <input value={it.description} onChange={e => updateItem(idx, { description: e.target.value })}
                        placeholder="Description"
                        className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2.5 py-1.5 text-xs" />
                      <input type="number" value={it.quantity} onChange={e => updateItem(idx, { quantity: e.target.value })}
                        placeholder="Qty" className="w-16 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                      <input type="number" step="0.01" value={it.unit_price} onChange={e => updateItem(idx, { unit_price: e.target.value })}
                        placeholder="Price" className="w-20 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                      <button onClick={() => removeItem(idx)} className="p-1 text-[#7d8590] hover:text-rose-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create quote"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {matrixOpen && detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-white font-semibold flex items-center gap-1.5"><Grid3x3 className="w-4 h-4 text-[#7d8590]" /> Estimation matrix</h2>
              <button onClick={() => setMatrixOpen(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-[#7d8590] mb-4">Pick a role, set hours, and the rate fills in from your activity types. Applying this replaces the quote's line items.</p>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
              <div className="grid grid-cols-12 gap-1.5 text-[10px] uppercase tracking-wide text-[#7d8590] px-1">
                <div className="col-span-4">Role / activity</div>
                <div className="col-span-4">Custom label</div>
                <div className="col-span-2">Hours</div>
                <div className="col-span-2">Rate</div>
              </div>
              {matrixRows.map((row, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                  <select value={row.activity_type_id || ""} onChange={e => pickActivityType(idx, e.target.value)}
                    className="col-span-4 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs">
                    <option value="">Custom…</option>
                    {activityTypes.map(a => <option key={a.activity_type_id} value={a.activity_type_id}>{a.name}</option>)}
                  </select>
                  <input value={row.label} onChange={e => updateMatrixRow(idx, { label: e.target.value })}
                    placeholder="Label" className="col-span-4 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                  <input type="number" value={row.hours} onChange={e => updateMatrixRow(idx, { hours: e.target.value })}
                    className="col-span-2 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                  <div className="col-span-2 flex items-center gap-1">
                    <input type="number" step="0.01" value={row.rate} onChange={e => updateMatrixRow(idx, { rate: e.target.value })}
                      className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                    <button onClick={() => removeMatrixRow(idx)} className="p-1 text-[#7d8590] hover:text-rose-400 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addMatrixRow} className="text-xs text-[#00F5D4] hover:underline mb-4">+ Add row</button>
            <div className="flex items-center justify-between border-t border-[#21262d] pt-4 mb-4">
              <span className="text-sm text-[#7d8590]">Matrix total</span>
              <span className="text-lg font-bold text-white">{fmt(matrixTotal, detail.currency)}</span>
            </div>
            <div className="flex gap-2">
              <Button onClick={applyMatrix} disabled={matrixSaving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {matrixSaving ? "Applying…" : "Apply to line items"}
              </Button>
              <Button onClick={() => setMatrixOpen(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
